import { ChangeDetectionStrategy, Component, HostBinding, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import Fuse from 'fuse.js';
import { sort } from 'remeda';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith, take } from 'rxjs/operators';
import { SnackBarService } from 'src/app/shared/snackbar.service';
import {
  DriverDetail,
  LoHiRecommendedShiftDetail,
  RecommendedShiftBucket,
  RecommendedShiftRegion,
} from 'src/app/types';
import { sortByAlpha } from 'src/app/utilities/commonSorters';
import { fuse } from 'src/app/utilities/fuse';
import { ShiftBookingService } from '../shift-booking.service';

const searchOptions: Fuse.IFuseOptions<DriverDetail> = {
  distance: 100,
  keys: ['driverName', 'phoneNumber'],
  location: 0,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.2,
};

@Component({
  selector: 'lohi-shift-booking',
  templateUrl: './shift-booking.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShiftBookingComponent implements OnInit {
  @HostBinding('class.lg:h-full') public hFull = true;
  @HostBinding('class.h-screen') public hScreen = true;
  public regions$: Observable<RecommendedShiftRegion[]>;
  public selectedRecommendationGroup$$ = new BehaviorSubject<RecommendedShiftRegion>(null);
  public recommendationTimes$$ = new BehaviorSubject<RecommendedShiftBucket[]>(null);
  public selectedRecommendation$$ = new BehaviorSubject<LoHiRecommendedShiftDetail>(null);
  public driversWhoCanBookShift$$ = new BehaviorSubject<DriverDetail[]>([]);
  public filteredDriversWhoCanBook$: Observable<DriverDetail[]>;
  public driverSearch = new FormControl(null);
  public hseConfirmed = new FormControl(false);
  public selectedDriverForm = new FormControl(null);
  public bookingShift$$ = new BehaviorSubject<boolean>(false);
  constructor(private shiftBookingService: ShiftBookingService, private snackBarService: SnackBarService) {}

  public ngOnInit(): void {
    this.regions$ = this.shiftBookingService.shiftRegions$;
    this.filteredDriversWhoCanBook$ = combineLatest([
      this.driversWhoCanBookShift$$.pipe(map(fuse(searchOptions))),
      this.driverSearch.valueChanges.pipe(startWith(null as string)),
    ]).pipe(
      map(([fused, searchTerm]) => {
        if (!searchTerm) {
          return fused.data;
        }
        return fused.fuse.search(searchTerm).map((i) => i.item);
      }),
    );
    this.selectedDriverForm.valueChanges.pipe(distinctUntilChanged()).subscribe((driver) => {
      this.hseConfirmed.setValue(false);
    });
  }

  public selectRecommendation(id: number) {
    this.shiftBookingService.loadTimesForRecommendation(id).then((results) => this.recommendationTimes$$.next(results));
    this.regions$
      .pipe(take(1))
      .toPromise()
      .then((regions) => {
        this.selectedRecommendationGroup$$.next(regions.find((region) => region.recommendedShiftId === id));
      });
  }

  public selectTime(startTime: string) {
    if (!this.selectedRecommendationGroup$$.value) {
      return;
    }
    this.selectedRecommendation$$.next(null);
    this.driversWhoCanBookShift$$.next([]);
    this.selectedDriverForm.setValue(null);
    this.hseConfirmed.setValue(false);
    this.shiftBookingService
      .loadRecommendationDetail(this.selectedRecommendationGroup$$.value.recommendedShiftId, startTime)
      .then((results) => {
        this.selectedRecommendation$$.next(results);
        return this.shiftBookingService.loadDriversForRecommendation(results.id);
      })
      .then((drivers) => {
        this.driversWhoCanBookShift$$.next(sort(drivers, sortByAlpha(['driverName'])));
      });
  }

  public async bookShiftForDriver() {
    if (!this.selectedRecommendation$$.value || !this.selectedDriverForm.value?.phoneNumber) {
      return;
    }
    this.bookingShift$$.next(true);

    const success = await this.shiftBookingService.bookShiftForDriver(
      this.selectedRecommendation$$.value.id,
      this.selectedDriverForm.value.phoneNumber,
    );
    this.bookingShift$$.next(false);
    if (success) {
      this.snackBarService.showMessage('Shift Booked!');
      if (this.selectedRecommendationGroup$$.value) {
        this.selectRecommendation(this.selectedRecommendationGroup$$.value.recommendedShiftId);
        this.selectedRecommendation$$.next(null);
        this.driversWhoCanBookShift$$.next([]);
        this.selectedDriverForm.setValue(null);
        this.hseConfirmed.setValue(false);
      } else {
        this.clearSelectedGroup();
      }
    } else {
      this.snackBarService.showError('Failed to book shift');
    }
  }

  public clearSelectedGroup() {
    this.selectedRecommendationGroup$$.next(null);
    this.recommendationTimes$$.next(null);
    this.selectedRecommendation$$.next(null);
    this.driversWhoCanBookShift$$.next([]);
    this.selectedDriverForm.setValue(null);
    this.hseConfirmed.setValue(false);
  }
}
